import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContent } from '../../contexts/ContentContext';
import { useTranslation } from '../../i18n';
import useWeddingSlug from '../../hooks/useWeddingSlug';
import Icon from '../AppIcon';

const BottomFloatingMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug, subdomain } = useWeddingSlug();
  const { content } = useContent();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('home');

  const basePath = subdomain ? '' : (slug ? `/w/${slug}` : '');

  const menuItems = useMemo(() => {
    const items = [
      { id: 'home', label: t('nav.home'), icon: 'Home', path: basePath || '/' },
      { id: 'rsvp', label: t('nav.rsvp'), icon: 'Calendar', path: `${basePath}/rsvp` },
    ];

    // Add Schedule if enabled
    if (content?.schedule?.enabled) {
      items.push({ id: 'schedule', label: t('nav.schedule'), icon: 'Clock', path: `${basePath}/schedule` });
    }

    // Add Info if enabled
    if (content?.extraInfo?.enabled) {
      items.push({ id: 'info', label: t('nav.info'), icon: 'Info', path: `${basePath}/info` });
    }

    // Add Gallery if URL is provided
    if (content?.gallery?.publicGalleryUrl) {
      items.push({
        id: 'gallery',
        label: content?.gallery?.galleryTitle || t('nav.gallery'),
        icon: 'Camera',
        path: `${basePath}/gallery`,
      });
    }

    return items;
  }, [content, basePath, t]);

  useEffect(() => {
    const currentPath = location?.pathname;
    const currentHash = location?.hash?.replace('#', '');

    if (currentPath.endsWith('/rsvp')) {
      setActiveSection('rsvp');
    } else if (currentPath.endsWith('/gallery')) {
      setActiveSection('gallery');
    } else if (currentPath.endsWith('/schedule')) {
      setActiveSection('schedule');
    } else if (currentPath.endsWith('/info')) {
      setActiveSection('info');
    } else if (currentHash) {
      setActiveSection(currentHash);
    } else {
      setActiveSection('home');
    }
  }, [location]);

  const handleNavigation = (item) => {
    setActiveSection(item?.id);

    // Preserve URL search params (e.g. ?guest=, ?guests=, ?invitation_type=)
    const searchParams = location?.search || '';

    if (item?.external) {
      // Open external links in new tab
      window.open(item?.path, '_blank', 'noopener,noreferrer');
    } else if (item?.path?.includes('#')) {
      const [path, hash] = item?.path?.split('#');
      navigate(path + searchParams);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      navigate(item?.path + searchParams);
    }
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-1000 bg-card shadow-romantic-lg border-t border-border"
      role="navigation"
      aria-label={t('nav.guestNavigation')}
    >
      <div className="max-w-screen-xl mx-auto px-4">
        <ul className="flex items-center justify-around h-20">
          {menuItems?.map((item) => (
            <li key={item?.id} className="flex-1">
              <button
                onClick={() => handleNavigation(item)}
                className={`
                  w-full flex flex-col items-center justify-center gap-1 py-2 px-3
                  transition-romantic hover-lift press-scale
                  ${activeSection === item?.id
                    ? 'text-primary' :'text-muted-foreground hover:text-foreground'
                  }
                `}
                aria-label={item?.label}
                aria-current={activeSection === item?.id ? 'page' : undefined}
              >
                <Icon
                  name={item?.icon}
                  size={24}
                  strokeWidth={activeSection === item?.id ? 2.5 : 2}
                />
                <span className="font-caption text-xs font-medium">
                  {item?.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default BottomFloatingMenu;
